import { GESTURE } from '@config/constants';
import './GestureReference.css';

/* ==========================================================================
 * GestureReference — in-app explainer / diagnostic.
 *
 * Every skeleton below is hand-authored in MediaPipe's 21-landmark
 * normalised space (x∈[0,1] right, y∈[0,1] down) so the visuals match
 * EXACTLY what the detectors consume. Numbers shown in callouts are
 * computed from the SAME landmark arrays via the SAME formulas used
 * by `PluckDetector.ts` and `GestureClassifier.ts`, not hard-coded.
 *
 * The goal is "what should I aim for?" not "how do I tune sliders?":
 * a player reads the skeleton, mirrors the pose, and the detector
 * fires. When it doesn't fire, comparing a real landmark overlay
 * against this reference shows what's off.
 * ======================================================================== */

type Lm = { x: number; y: number };
type Pose = readonly Lm[]; // 21 points

/** Bones, as [a,b] pairs into the 21-landmark array. */
const BONES: readonly [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4],         // thumb
  [0, 5], [5, 6], [6, 7], [7, 8],          // index
  [5, 9], [9, 10], [10, 11], [11, 12],     // middle
  [9, 13], [13, 14], [14, 15], [15, 16],   // ring
  [13, 17], [17, 18], [18, 19], [19, 20],  // pinky
  [0, 17],                                 // palm edge
];

const FINGER_TIPS = { thumb: 4, index: 8, middle: 12, ring: 16, pinky: 20 };
const FINGER_PIPS = { thumb: 3, index: 6, middle: 10, ring: 14, pinky: 18 };
const FINGER_MCPS = { thumb: 2, index: 5, middle: 9, ring: 13, pinky: 17 };

/** Build a pose from terse (x,y) pairs. */
const p = (pairs: number[][]): Pose => pairs.map(([x, y]) => ({ x: x!, y: y! }));

/* --- REFERENCE / baseline poses ------------------------------------------ */

/** Relaxed vertical hand, fingers extended — the "aim-for-this" shape when
 *  playing. Matches `play_stance` from the calibration script. */
const POSE_REFERENCE: Pose = p([
  [0.50, 0.95], // 0 wrist
  [0.38, 0.88], // 1 thumb CMC
  [0.30, 0.78], // 2 thumb MCP
  [0.24, 0.68], // 3 thumb IP
  [0.20, 0.58], // 4 thumb TIP
  [0.40, 0.75], // 5 index MCP
  [0.40, 0.60], // 6 index PIP
  [0.40, 0.48], // 7 index DIP
  [0.40, 0.38], // 8 index TIP
  [0.50, 0.74], // 9 middle MCP
  [0.50, 0.58], // 10 middle PIP
  [0.50, 0.45], // 11 middle DIP
  [0.50, 0.33], // 12 middle TIP
  [0.60, 0.75], // 13 ring MCP
  [0.60, 0.60], // 14 ring PIP
  [0.60, 0.48], // 15 ring DIP
  [0.60, 0.38], // 16 ring TIP
  [0.68, 0.80], // 17 pinky MCP
  [0.68, 0.68], // 18 pinky PIP
  [0.68, 0.58], // 19 pinky DIP
  [0.68, 0.50], // 20 pinky TIP
]);

/* --- PLUCK — accepted: flick ; rejected: translate ---------------------- */

/** Index finger MID-pluck: TIP has just curled past PIP (y> PIP.y),
 *  MCP unchanged. Thumb/middle/ring still upright. */
const POSE_PLUCK_ACCEPT: Pose = p([
  [0.50, 0.95], [0.38, 0.88], [0.30, 0.78], [0.24, 0.68], [0.20, 0.58],
  [0.40, 0.75],        // 5 index MCP — unchanged
  [0.40, 0.62],        // 6 index PIP
  [0.40, 0.65],        // 7 index DIP — curled back
  [0.40, 0.68],        // 8 index TIP — below PIP = plucked through
  [0.50, 0.74], [0.50, 0.58], [0.50, 0.45], [0.50, 0.33],
  [0.60, 0.75], [0.60, 0.60], [0.60, 0.48], [0.60, 0.38],
  [0.68, 0.80], [0.68, 0.68], [0.68, 0.58], [0.68, 0.50],
]);

/** Translation: same finger posture as reference, but whole hand shifted
 *  down by 0.10 → TIP and MCP both fell together. Tip didn't cross PIP. */
const POSE_PLUCK_REJECT: Pose = POSE_REFERENCE.map((pt) => ({ x: pt.x, y: pt.y + 0.10 }));

/* --- GLISS — accepted: slide ; rejected: cupped (bent) ------------------ */

const POSE_GLISS_ACCEPT: Pose = p([
  [0.50, 0.80], [0.38, 0.73], [0.30, 0.63], [0.24, 0.53], [0.20, 0.43],
  [0.40, 0.60], [0.40, 0.45], [0.40, 0.33], [0.40, 0.23],
  [0.50, 0.59], [0.50, 0.43], [0.50, 0.30], [0.50, 0.18],
  [0.60, 0.60], [0.60, 0.45], [0.60, 0.33], [0.60, 0.23],
  [0.68, 0.65], [0.68, 0.53], [0.68, 0.43], [0.68, 0.35],
]);

/** All 3 pluck fingers cupped (TIP below PIP) → hand-level allThreeBent
 *  gate fires → gliss rejected even if hand is translating. */
const POSE_GLISS_REJECT: Pose = p([
  [0.50, 0.95], [0.38, 0.88], [0.30, 0.78], [0.24, 0.75], [0.24, 0.82],
  [0.40, 0.75], [0.40, 0.62], [0.40, 0.66], [0.40, 0.70], // index bent
  [0.50, 0.74], [0.50, 0.60], [0.50, 0.64], [0.50, 0.68], // middle bent
  [0.60, 0.75], [0.60, 0.60], [0.60, 0.64], [0.60, 0.68], // ring bent
  [0.68, 0.80], [0.68, 0.68], [0.68, 0.58], [0.68, 0.50],
]);

/* --- BEND — accepted: aligned cup ; rejected: splayed ------------------- */

/** 3 fingers (index, middle, ring) aligned + curled. Tips at same y. */
const POSE_BEND_ACCEPT: Pose = p([
  [0.50, 0.92], [0.38, 0.85], [0.30, 0.75], [0.24, 0.66], [0.22, 0.60],
  [0.40, 0.72], [0.42, 0.58], [0.43, 0.52], [0.44, 0.50], // index tip
  [0.50, 0.72], [0.52, 0.58], [0.52, 0.52], [0.52, 0.50], // middle tip
  [0.60, 0.72], [0.58, 0.58], [0.58, 0.52], [0.58, 0.50], // ring tip
  [0.68, 0.78], [0.68, 0.67], [0.68, 0.58], [0.68, 0.52],
]);

/** Splayed fingers — tip y-coordinates spread > 0.09 → rejected. */
const POSE_BEND_REJECT: Pose = p([
  [0.50, 0.92], [0.38, 0.85], [0.30, 0.75], [0.24, 0.66], [0.22, 0.60],
  [0.36, 0.74], [0.36, 0.60], [0.36, 0.50], [0.34, 0.42], // index tip HIGH
  [0.50, 0.74], [0.50, 0.58], [0.50, 0.46], [0.50, 0.36], // middle tip HIGHER
  [0.62, 0.74], [0.64, 0.62], [0.66, 0.54], [0.68, 0.48], // ring tip LOWER
  [0.70, 0.80], [0.70, 0.68], [0.70, 0.58], [0.70, 0.50],
]);

/* --- MUTE / command-palm ------------------------------------------------- */

/** Palm-back (command mode). Rendered as hand with back showing — a fist-like
 *  silhouette with all fingers curled inward. */
const POSE_MUTE_ACCEPT: Pose = p([
  [0.50, 0.90], [0.62, 0.82], [0.68, 0.72], [0.65, 0.64], [0.58, 0.62],
  [0.42, 0.72], [0.42, 0.62], [0.44, 0.66], [0.46, 0.70],
  [0.50, 0.72], [0.50, 0.62], [0.52, 0.66], [0.54, 0.70],
  [0.58, 0.72], [0.58, 0.62], [0.56, 0.66], [0.54, 0.70],
  [0.65, 0.78], [0.66, 0.70], [0.64, 0.72], [0.62, 0.74],
]);

/** Palm-front (playing pose) — same as reference. */
const POSE_MUTE_REJECT: Pose = POSE_REFERENCE;

/* --- Formulas (match PluckDetector.ts / GestureClassifier.ts) ----------- */

const dist = (a: Lm, b: Lm): number => Math.hypot(a.x - b.x, a.y - b.y);

const palmCenter = (lm: Pose): Lm => {
  const idx = [0, 5, 9, 13, 17];
  let sx = 0, sy = 0;
  for (const i of idx) { sx += lm[i]!.x; sy += lm[i]!.y; }
  return { x: sx / idx.length, y: sy / idx.length };
};

const fingerCurl = (lm: Pose, finger: keyof typeof FINGER_TIPS): number => {
  const palm = palmCenter(lm);
  const tip = lm[FINGER_TIPS[finger]]!;
  const mcp = lm[FINGER_MCPS[finger]]!;
  const tipToPalm = dist(tip, palm);
  const mcpToPalm = dist(mcp, palm);
  if (mcpToPalm < 1e-6) return 0;
  return 1 - tipToPalm / (mcpToPalm * 1.8);
};

const bendYSpread = (lm: Pose): number => {
  const ys = [lm[FINGER_TIPS.index]!.y, lm[FINGER_TIPS.middle]!.y, lm[FINGER_TIPS.ring]!.y];
  return Math.max(...ys) - Math.min(...ys);
};

const tipBelowPip = (lm: Pose, finger: keyof typeof FINGER_TIPS): boolean =>
  lm[FINGER_TIPS[finger]]!.y > lm[FINGER_PIPS[finger]]!.y;

const palmBackCross = (lm: Pose): number => {
  const w = lm[0]!, i = lm[5]!, pk = lm[17]!;
  return (i.x - w.x) * (pk.y - w.y) - (i.y - w.y) * (pk.x - w.x);
};

/* --- SVG hand renderer --------------------------------------------------- */

interface HandSVGProps {
  pose: Pose;
  w?: number;
  h?: number;
  /** Optional per-landmark highlight colors, keyed by index. */
  hi?: Record<number, string>;
  /** Optional arrows to draw from landmark index → (dx,dy in normalised). */
  arrows?: Array<{ from: number; dx: number; dy: number; label?: string; color?: string }>;
  /** Optional horizontal lines (string positions) at y-values 0..1. */
  stringYs?: number[];
  /** Optional landmark labels to render next to dots. */
  labels?: Record<number, string>;
  className?: string;
}

function HandSVG({ pose, w = 200, h = 220, hi, arrows, stringYs, labels, className }: HandSVGProps) {
  const X = (x: number) => x * w;
  const Y = (y: number) => y * h;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className={`grf-hand ${className ?? ''}`}>
      {stringYs?.map((sy, i) => (
        <line
          key={`s${i}`}
          x1={0}
          x2={w}
          y1={Y(sy)}
          y2={Y(sy)}
          stroke="rgba(255,210,74,0.35)"
          strokeWidth={0.6}
        />
      ))}
      {BONES.map(([a, b], i) => (
        <line
          key={`b${i}`}
          x1={X(pose[a]!.x)}
          y1={Y(pose[a]!.y)}
          x2={X(pose[b]!.x)}
          y2={Y(pose[b]!.y)}
          stroke="rgba(255,255,255,0.55)"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
      ))}
      {pose.map((pt, i) => (
        <circle
          key={`p${i}`}
          cx={X(pt.x)}
          cy={Y(pt.y)}
          r={i === 0 || i === 5 || i === 9 || i === 13 || i === 17 ? 3.5 : 2.2}
          fill={hi?.[i] ?? 'rgba(255,255,255,0.85)'}
        />
      ))}
      {labels && Object.entries(labels).map(([idx, txt]) => {
        const i = Number(idx);
        return (
          <text
            key={`l${i}`}
            x={X(pose[i]!.x) + 5}
            y={Y(pose[i]!.y) - 3}
            fontSize={9}
            fill="rgba(255,255,255,0.7)"
          >{txt}</text>
        );
      })}
      {arrows?.map((a, i) => {
        const x0 = X(pose[a.from]!.x);
        const y0 = Y(pose[a.from]!.y);
        const x1 = X(pose[a.from]!.x + a.dx);
        const y1 = Y(pose[a.from]!.y + a.dy);
        return (
          <g key={`a${i}`}>
            <line
              x1={x0} y1={y0} x2={x1} y2={y1}
              stroke={a.color ?? '#ffd24a'}
              strokeWidth={1.8}
              markerEnd={`url(#grf-arrow-${a.color === '#7dd3fc' ? 'blue' : 'gold'})`}
            />
            {a.label && (
              <text
                x={(x0 + x1) / 2 + 6}
                y={(y0 + y1) / 2}
                fontSize={9}
                fill={a.color ?? '#ffd24a'}
              >{a.label}</text>
            )}
          </g>
        );
      })}
      <defs>
        <marker id="grf-arrow-gold" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#ffd24a" />
        </marker>
        <marker id="grf-arrow-blue" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#7dd3fc" />
        </marker>
      </defs>
    </svg>
  );
}

/* --- Example card --------------------------------------------------------- */

interface ExampleProps {
  verdict: 'accept' | 'reject';
  title: string;
  pose: Pose;
  arrows?: HandSVGProps['arrows'];
  hi?: HandSVGProps['hi'];
  stringYs?: number[];
  /** Computed numbers to show (already stringified with units). */
  stats: Array<{ k: string; v: string; ok?: boolean }>;
  reason: string;
}

function Example({ verdict, title, pose, arrows, hi, stringYs, stats, reason }: ExampleProps) {
  return (
    <div className={`grf-ex grf-ex--${verdict}`}>
      <div className="grf-ex__hd">
        <span className="grf-ex__verdict">{verdict === 'accept' ? '✓ Accepted' : '✗ Rejected'}</span>
        <span className="grf-ex__title">{title}</span>
      </div>
      <HandSVG pose={pose} arrows={arrows} hi={hi} stringYs={stringYs} />
      <div className="grf-ex__stats">
        {stats.map((s, i) => (
          <div key={i} className={`grf-ex__stat ${s.ok === true ? 'ok' : s.ok === false ? 'bad' : ''}`}>
            <span className="grf-ex__stat-k">{s.k}</span>
            <span className="grf-ex__stat-v">{s.v}</span>
          </div>
        ))}
      </div>
      <div className="grf-ex__reason">{reason}</div>
    </div>
  );
}

/* --- Main panel ---------------------------------------------------------- */

export function GestureReference() {
  // -- Pluck: velocities are IMAGINED for illustration, shown as arrows.
  //    Ratio & minVelocity numbers are computed against the same constants
  //    the detector uses so they stay in sync.
  const PLUCK_MIN_L = GESTURE.PLUCK_MIN_VELOCITY_LEFT;
  const PLUCK_MIN_R = GESTURE.PLUCK_MIN_VELOCITY_RIGHT;
  const MCP_GATE = 0.12; // MCP_MIN_TRANSLATE_VEL in PluckDetector.ts
  const RATIO = 3.0;     // FLICK_TIP_MCP_RATIO

  const STRING_YS = [0.28, 0.40, 0.52, 0.64, 0.76];

  // Numerical reads from the reference skeletons (match real formulas).
  const bendAccSpread = bendYSpread(POSE_BEND_ACCEPT);
  const bendRejSpread = bendYSpread(POSE_BEND_REJECT);
  const bendAccCurl = (fingerCurl(POSE_BEND_ACCEPT, 'index') + fingerCurl(POSE_BEND_ACCEPT, 'middle') + fingerCurl(POSE_BEND_ACCEPT, 'ring')) / 3;
  const muteBack = palmBackCross(POSE_MUTE_ACCEPT);
  const muteFront = palmBackCross(POSE_MUTE_REJECT);

  return (
    <div className="grf">
      <p className="grf-intro">
        Below is the exact hand shape the detectors are calibrated against.
        Every number in the examples is computed from the drawn skeleton
        using the <em>same formulas</em> the engine runs — if your live hand
        matches a pose, that classification will fire.
      </p>

      {/* ===== Reference ================================================ */}
      <section className="grf-sec">
        <h4 className="grf-sec__h">Reference pose — neutral play stance</h4>
        <div className="grf-ref">
          <HandSVG
            pose={POSE_REFERENCE}
            w={260}
            h={290}
            labels={{
              0: 'wrist (0)',
              4: 'thumb TIP (4)',
              8: 'index TIP (8)',
              12: 'middle TIP (12)',
              16: 'ring TIP (16)',
              5: 'index MCP (5)',
              6: 'PIP (6)',
              17: 'pinky MCP (17)',
            }}
            stringYs={STRING_YS}
          />
          <ul className="grf-ref__legend">
            <li>
              <b>Pluck</b> — TIP (8/12) crosses its own PIP (6/10) <em>downward</em>
              while MCP (5/9) stays put. Velocity ratio <code>|velTip| / |velMCP| ≥ {RATIO}</code>.
              Min |velTip|: LH {PLUCK_MIN_L.toFixed(2)}, RH {PLUCK_MIN_R.toFixed(2)}.
            </li>
            <li>
              <b>Glissando</b> — TIPs extended, whole hand translating. Ratio below{' '}
              <code>{RATIO}</code> AND <code>|velMCP| ≥ {MCP_GATE}</code>. Every string
              Y the tip crosses fires.
            </li>
            <li>
              <b>Bend</b> — index/middle/ring tips aligned on Y{' '}
              (<code>max − min ≤ {GESTURE.BEND_ALIGN_THRESHOLD.toFixed(3)}</code>) AND
              all three curls ≥ <code>{GESTURE.BEND_EXTENDED_THRESHOLD}</code>.
            </li>
            <li>
              <b>Mute / command</b> — back of hand to camera. Cross-product sign of
              <code> (idxMCP − wrist) × (pinkyMCP − wrist)</code> flips based on palm
              orientation.
            </li>
            <li>
              Yellow horizontals = string positions. Pluck / gliss events fire on Y-crossings.
            </li>
          </ul>
        </div>
      </section>

      {/* ===== Pluck =================================================== */}
      <section className="grf-sec">
        <h4 className="grf-sec__h">Pluck (flick)</h4>
        <div className="grf-row">
          <Example
            verdict="accept"
            title="Finger curls past PIP, MCP still"
            pose={POSE_PLUCK_ACCEPT}
            stringYs={STRING_YS}
            hi={{ 8: '#4ade80', 6: '#4ade80', 5: '#ffd24a' }}
            arrows={[
              { from: 8, dx: 0, dy: 0.02, label: 'velTip ≈ 0.80', color: '#4ade80' },
              { from: 5, dx: 0, dy: 0.01, label: 'velMCP ≈ 0.05', color: '#ffd24a' },
            ]}
            stats={[
              { k: 'TIP below PIP?', v: 'yes', ok: true },
              { k: '|velTip|', v: `0.80 ≥ ${PLUCK_MIN_L.toFixed(2)}`, ok: true },
              { k: '|velTip|/|velMCP|', v: `16 ≥ ${RATIO}`, ok: true },
            ]}
            reason="Knuckle stays put, tip pivots fast. Classic pluck — single string fires."
          />
          <Example
            verdict="reject"
            title="Whole hand drops (translation)"
            pose={POSE_PLUCK_REJECT}
            stringYs={STRING_YS}
            hi={{ 8: '#ff6b6b', 5: '#ff6b6b' }}
            arrows={[
              { from: 8, dx: 0, dy: 0.05, label: 'velTip ≈ 0.60', color: '#ff6b6b' },
              { from: 5, dx: 0, dy: 0.05, label: 'velMCP ≈ 0.50', color: '#ff6b6b' },
            ]}
            stats={[
              { k: 'TIP below PIP?', v: 'no', ok: false },
              { k: '|velTip|/|velMCP|', v: `1.2 < ${RATIO}`, ok: false },
            ]}
            reason="Tip and MCP move together → classifier reclassifies as glissando, not pluck."
          />
        </div>
      </section>

      {/* ===== Gliss =================================================== */}
      <section className="grf-sec">
        <h4 className="grf-sec__h">Glissando (sweep)</h4>
        <div className="grf-row">
          <Example
            verdict="accept"
            title="Fingers extended, hand translating"
            pose={POSE_GLISS_ACCEPT}
            stringYs={STRING_YS}
            hi={{ 5: '#4ade80', 9: '#4ade80', 13: '#4ade80' }}
            arrows={[
              { from: 9, dx: 0, dy: 0.08, label: 'velMCP ≈ 0.30', color: '#4ade80' },
            ]}
            stats={[
              { k: 'all 3 tips bent?', v: 'no', ok: true },
              { k: '|velMCP|', v: `0.30 ≥ ${MCP_GATE}`, ok: true },
              { k: 'velTip/velMCP', v: `1.1 < ${RATIO}`, ok: true },
            ]}
            reason="Every string whose Y the tip crosses emits one event. Same refractory on reverse direction lets sweep-back-and-forth fire cleanly."
          />
          <Example
            verdict="reject"
            title="All 3 fingers bent (cupped hand)"
            pose={POSE_GLISS_REJECT}
            stringYs={STRING_YS}
            hi={{ 8: '#ff6b6b', 12: '#ff6b6b', 16: '#ff6b6b' }}
            stats={[
              { k: 'index TIP > PIP', v: 'yes', ok: false },
              { k: 'middle TIP > PIP', v: 'yes', ok: false },
              { k: 'ring TIP > PIP', v: 'yes', ok: false },
            ]}
            reason="All three pluck fingers curled → classifier enters BEND mode. Gliss path is suppressed even if MCP is translating."
          />
        </div>
      </section>

      {/* ===== Bend ==================================================== */}
      <section className="grf-sec">
        <h4 className="grf-sec__h">Bend (pitch shift)</h4>
        <div className="grf-row">
          <Example
            verdict="accept"
            title="3 fingers aligned + curled"
            pose={POSE_BEND_ACCEPT}
            hi={{ 8: '#4ade80', 12: '#4ade80', 16: '#4ade80' }}
            stats={[
              { k: 'TIP Y-spread', v: `${bendAccSpread.toFixed(3)} ≤ ${GESTURE.BEND_ALIGN_THRESHOLD.toFixed(3)}`, ok: true },
              { k: 'mean curl', v: `${bendAccCurl.toFixed(2)} ≥ ${GESTURE.BEND_EXTENDED_THRESHOLD}`, ok: true },
            ]}
            reason="Three tips share a Y line within tolerance; fingers all cupped — classifier enters bend mode and tracks vertical motion → cents."
          />
          <Example
            verdict="reject"
            title="Splayed fingers"
            pose={POSE_BEND_REJECT}
            hi={{ 8: '#ff6b6b', 12: '#ff6b6b', 16: '#ff6b6b' }}
            stats={[
              { k: 'TIP Y-spread', v: `${bendRejSpread.toFixed(3)} > ${GESTURE.BEND_ALIGN_THRESHOLD.toFixed(3)}`, ok: false },
            ]}
            reason="Index high, middle higher, ring lower. Y-spread exceeds threshold → not a bend pose."
          />
        </div>
      </section>

      {/* ===== Mute / command ========================================== */}
      <section className="grf-sec">
        <h4 className="grf-sec__h">Mute / command (palm-back)</h4>
        <div className="grf-row">
          <Example
            verdict="accept"
            title="Back of hand to camera"
            pose={POSE_MUTE_ACCEPT}
            hi={{ 5: '#4ade80', 17: '#4ade80', 0: '#4ade80' }}
            stats={[
              { k: 'cross((5-0),(17-0))', v: `${muteBack.toFixed(3)}`, ok: true },
              { k: 'fingers above wrist', v: 'yes', ok: true },
            ]}
            reason="Cross-product sign flips vs palm-front. Sound is hard-cut; pairing with N extended fingers switches set (LH) or tone (RH)."
          />
          <Example
            verdict="reject"
            title="Palm facing camera (normal play)"
            pose={POSE_MUTE_REJECT}
            hi={{ 5: '#ff6b6b', 17: '#ff6b6b' }}
            stats={[
              { k: 'cross((5-0),(17-0))', v: `${muteFront.toFixed(3)}`, ok: false },
            ]}
            reason="Play posture — detectors stay fully active, nothing is muted."
          />
        </div>
      </section>

      <p className="grf-footer">
        Live mismatch? Turn on <em>Show hand tracking</em> below and compare the live
        overlay against these skeletons.
      </p>
    </div>
  );
}
